import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Clock, Play, Square, Loader2, Plus, Pencil, Trash2, Scan, Download, MapPin } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { GlassCard, PageHeader, StatusBadge, EmptyState } from "../components/shared";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import jsQR from "jsqr";

function fmtMin(min) {
    if (!min) return "0h 0m";
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h ${m}m`;
}

export default function Attendance() {
    const { user } = useAuth();
    const [today, setToday] = useState(null);
    const [history, setHistory] = useState([]);
    const [team, setTeam] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [stats, setStats] = useState(null);
    const [adminOpen, setAdminOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(false);
    const [activeTab, setActiveTab] = useState("all"); // "all", "present", "absent"
    const canViewAll = ["super_admin", "hr", "manager", "lead"].includes(user?.role);
    const canManage = ["super_admin", "hr", "manager", "lead"].includes(user?.role);

    // QR scanner states and refs
    const [scannerOpen, setScannerOpen] = useState(false);
    const [facingMode, setFacingMode] = useState("environment");
    const [stream, setStream] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const scanRef = useRef(null);

    const startCamera = async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: facingMode }
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                videoRef.current.setAttribute("playsinline", true);
                videoRef.current.play();
            }
        } catch (err) {
            toast.error("Failed to access camera. Please check permissions.");
            setScannerOpen(false);
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        if (scanRef.current) {
            cancelAnimationFrame(scanRef.current);
            scanRef.current = null;
        }
    };

    useEffect(() => {
        if (scannerOpen) {
            startCamera();
        } else {
            stopCamera();
        }
        return () => stopCamera();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scannerOpen, facingMode]);

    const handleQRSuccess = async (qrData) => {
        stopCamera();
        setScannerOpen(false);
        
        toast.loading("Processing QR Code...", { id: "qr-scan" });
        try {
            const coords = await new Promise((res) =>
                navigator.geolocation
                    ? navigator.geolocation.getCurrentPosition(
                          (p) => res({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
                          () => res({ latitude: null, longitude: null }),
                          { timeout: 3000 },
                      )
                    : res({ latitude: null, longitude: null }),
            );
            
            const { data } = await api.post("/attendance/scan-qr", {
                qr_data: qrData,
                latitude: coords.latitude,
                longitude: coords.longitude
            });
            
            if (data.status === "punched_in") {
                toast.success(`Success: Punched In ${data.employee_name}!`, { id: "qr-scan" });
            } else {
                toast.success(`Success: Punched Out ${data.employee_name}! Duration: ${fmtMin(data.duration_minutes)}`, { id: "qr-scan" });
            }
            load();
        } catch (e) {
            toast.error(formatApiError(e), { id: "qr-scan" });
        }
    };

    useEffect(() => {
        if (stream && videoRef.current) {
            const scan = () => {
                if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
                    const canvas = canvasRef.current;
                    if (canvas) {
                        const ctx = canvas.getContext("2d");
                        canvas.height = videoRef.current.videoHeight;
                        canvas.width = videoRef.current.videoWidth;
                        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
                        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                        const code = jsQR(imageData.data, imageData.width, imageData.height, {
                            inversionAttempts: "dontInvert",
                        });
                        if (code) {
                            handleQRSuccess(code.data);
                            return;
                        }
                    }
                }
                scanRef.current = requestAnimationFrame(scan);
            };
            scanRef.current = requestAnimationFrame(scan);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stream]);

    const downloadIdCard = async () => {
        toast.loading("Generating ID Card...", { id: "id-download" });
        try {
            const response = await api.get(`/documents/id-card/${user.id}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `id_card_${user.name.replace(/\s+/g, '_')}.pdf`;
            link.click();
            toast.success("Downloaded ID Card PDF", { id: "id-download" });
        } catch (e) {
            toast.error("Failed to download ID Card", { id: "id-download" });
        }
    };

    const downloadCompanyQR = async () => {
        toast.loading("Generating Office QR PDF...", { id: "qr-download" });
        try {
            const response = await api.get('/documents/company-qr', { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'company_qr.pdf';
            link.click();
            toast.success("Downloaded Office QR PDF", { id: "qr-download" });
        } catch (e) {
            toast.error("Failed to download company QR code", { id: "qr-download" });
        }
    };

    const load = async () => {
        try {
            const [t, h, s, all, emp] = await Promise.all([
                api.get("/attendance/today"),
                api.get("/attendance/me?days=30"),
                canViewAll ? api.get("/attendance/stats") : Promise.resolve({ data: null }),
                canViewAll ? api.get("/attendance/all") : Promise.resolve({ data: { items: [] } }),
                canManage ? api.get("/employees?limit=500&status=active") : Promise.resolve({ data: { items: [] } }),
            ]);
            setToday(t.data);
            setHistory(h.data.items || []);
            if (s.data) setStats(s.data);
            setTeam(all.data.items || []);
            setEmployees(emp.data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    const exportToCSV = () => {
        let headers = [];
        let rows = [];
        let filename = "";
        const todayStr = new Date().toISOString().split("T")[0];

        if (activeTab === "absent") {
            const presentToday = team.filter((r) => r.date === todayStr);
            const presentUserIds = new Set(presentToday.map((r) => r.user_id));
            const absentToday = employees.filter((emp) => !presentUserIds.has(emp.id));

            headers = ["Employee Name", "Email", "Department", "Designation", "Status", "Date"];
            rows = absentToday.map((emp) => [
                emp.name,
                emp.email,
                emp.department || "—",
                emp.designation || "—",
                "Absent",
                todayStr,
            ]);
            filename = `absent_employees_${todayStr}.csv`;
        } else {
            const dataset = activeTab === "present" 
                ? team.filter((r) => r.date === todayStr) 
                : team;
            
            headers = ["Employee Name", "Date", "Punch In", "Punch Out", "Duration (Minutes)", "Late", "Status", "Lat In", "Lng In", "Lat Out", "Lng Out"];
            rows = dataset.map((h) => [
                h.user_name,
                h.date,
                h.punch_in ? new Date(h.punch_in).toLocaleTimeString() : "—",
                h.punch_out ? new Date(h.punch_out).toLocaleTimeString() : "—",
                h.duration_minutes || 0,
                h.is_late ? "Yes" : "No",
                h.status,
                h.lat_in || "—",
                h.lng_in || "—",
                h.lat_out || "—",
                h.lng_out || "—",
            ]);
            
            const dateSuffix = activeTab === "present" ? todayStr : "all_time";
            filename = `attendance_logs_${dateSuffix}.csv`;
        }

        // Generate CSV content
        const csvContent = [
            headers.join(","),
            ...rows.map((row) =>
                row
                    .map((val) => {
                        const strVal = String(val).replace(/"/g, '""');
                        return strVal.includes(",") || strVal.includes("\n") || strVal.includes('"')
                            ? `"${strVal}"`
                            : strVal;
                    })
                    .join(","),
            ),
        ].join("\n");

        // Trigger download with UTF-8 BOM
        const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Exported: ${filename}`);
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    const punch = async (which) => {
        setActing(true);
        try {
            const coords = await new Promise((res, rej) => {
                if (!navigator.geolocation) {
                    rej(new Error("Geolocation is not supported by your browser. Location access is mandatory to punch attendance."));
                    return;
                }
                navigator.geolocation.getCurrentPosition(
                    (p) => res({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
                    (err) => {
                        if (err.code === err.PERMISSION_DENIED) {
                            rej(new Error("Location permission denied. You must allow location access to punch attendance."));
                        } else if (err.code === err.TIMEOUT) {
                            rej(new Error("Location lookup timed out. Please ensure location services are enabled on your device and try again."));
                        } else {
                            rej(new Error("Unable to retrieve your location. Location settings must be enabled to record attendance."));
                        }
                    },
                    { enableHighAccuracy: true, timeout: 8000 },
                );
            });
            await api.post(`/attendance/punch-${which}`, coords);
            toast.success(`Punched ${which}!`);
            load();
        } catch (e) {
            toast.error(e.message || formatApiError(e), { duration: 6000 });
        } finally {
            setActing(false);
        }
    };

    const punchedIn = today?.punch_in;
    const punchedOut = today?.punch_out;

    const remove = async (id) => {
        if (!window.confirm("Delete this attendance record?")) return;
        try {
            await api.delete(`/attendance/${id}`);
            toast.success("Attendance deleted");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <PageHeader title="Attendance" subtitle="Track punch-ins, work hours and team presence." testId="attendance-header" />

            {/* Punch widget */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <GlassCard className="p-6 lg:col-span-2 relative overflow-hidden">
                    <div className="absolute -right-20 -bottom-20 w-60 h-60 rounded-full bg-primary/15 blur-3xl" />
                    <div className="relative">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Today's status</div>
                        <div className="font-heading text-4xl font-bold mt-2 mb-3">
                            {punchedOut ? "Punched out" : punchedIn ? "Working" : "Not punched in"}
                        </div>
                        <div className="text-sm text-muted-foreground mb-5">
                            {punchedIn && <span>In: {new Date(today.punch_in).toLocaleTimeString()}</span>}
                            {punchedOut && <span> · Out: {new Date(today.punch_out).toLocaleTimeString()}</span>}
                            {today?.duration_minutes ? <span> · {fmtMin(today.duration_minutes)}</span> : null}
                        </div>
                        <div className="flex gap-3 flex-wrap items-center">
                            <Button
                                variant="secondary"
                                onClick={() => setScannerOpen(true)}
                                className="rounded-full h-12 px-7 bg-primary/10 text-primary hover:bg-primary/20"
                            >
                                <Scan className="w-4 h-4 mr-1.5" /> Scan QR
                            </Button>
                            
                            <Button
                                variant="ghost"
                                onClick={downloadIdCard}
                                className="rounded-full h-12 px-5 text-xs font-semibold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                            >
                                <Download className="w-4.5 h-4.5 mr-1.5" /> ID Card QR
                            </Button>

                            {canManage && (
                                <Button
                                    variant="ghost"
                                    onClick={downloadCompanyQR}
                                    className="rounded-full h-12 px-5 text-xs font-semibold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                                >
                                    <Download className="w-4.5 h-4.5 mr-1.5" /> Office QR Code
                                </Button>
                            )}

                            {today?.is_late && <span className="self-center text-xs text-warning font-semibold">⚠ Late punch</span>}
                        </div>
                    </div>
                </GlassCard>

                {canViewAll && stats && (
                    <GlassCard className="p-6">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Today's overview</div>
                        <div className="mt-4 space-y-3">
                            <Row label="Present" value={stats.present} color="text-success" />
                            <Row label="Absent" value={stats.absent} color="text-destructive" />
                            <Row label="Late" value={stats.late} color="text-warning" />
                            <Row label="Total" value={stats.total} />
                        </div>
                    </GlassCard>
                )}
            </div>

            {canManage && (
                <div className="mb-6">
                    <Dialog open={adminOpen} onOpenChange={(open) => { setAdminOpen(open); if (!open) setEditing(null); }}>
                        <DialogTrigger asChild>
                            <Button className="rounded-full" data-testid="admin-attendance-add">
                                <Plus className="w-4 h-4 mr-1" /> Add attendance
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>{editing ? "Edit attendance" : "Add attendance"}</DialogTitle></DialogHeader>
                            <AttendanceForm
                                employees={employees}
                                record={editing}
                                onSaved={() => { setAdminOpen(false); setEditing(null); load(); }}
                            />
                        </DialogContent>
                    </Dialog>
                </div>
            )}

            {/* History */}
            <GlassCard className="p-6">
                <h3 className="font-heading font-semibold text-lg mb-4">My attendance — last 30 days</h3>
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> :
                history.length === 0 ? (
                    <EmptyState icon={Clock} title="No history" description="Start punching in to see your records here." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                    <th className="text-left p-3">Date</th>
                                    <th className="text-left p-3">Punch In</th>
                                    <th className="text-left p-3">Punch Out</th>
                                    <th className="text-left p-3">Duration</th>
                                    <th className="text-left p-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h) => (
                                    <tr key={h.id} className="border-t border-black/5 dark:border-white/5">
                                        <td className="p-3">{h.date}</td>
                                        <td className="p-3">{h.punch_in ? new Date(h.punch_in).toLocaleTimeString() : "—"}</td>
                                        <td className="p-3">{h.punch_out ? new Date(h.punch_out).toLocaleTimeString() : "—"}</td>
                                        <td className="p-3">{fmtMin(h.duration_minutes)}</td>
                                        <td className="p-3">
                                            {h.is_late ? <span className="text-xs text-warning font-semibold">Late</span> : <StatusBadge status={h.status} />}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </GlassCard>

            {canViewAll && (
                <GlassCard className="p-6 mt-6">
                    {(() => {
                        const todayStr = new Date().toISOString().split("T")[0];
                        const presentToday = team.filter((r) => r.date === todayStr);
                        const presentUserIds = new Set(presentToday.map((r) => r.user_id));
                        const absentToday = employees.filter((emp) => !presentUserIds.has(emp.id));

                        return (
                            <>
                                <div className="flex items-center justify-between flex-wrap gap-4 border-b border-black/5 dark:border-white/5 pb-4 mb-4">
                                    <div className="flex items-center gap-3">
                                        <h3 className="font-heading font-semibold text-lg text-slate-800 dark:text-slate-100">Team Attendance</h3>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={exportToCSV}
                                            className="rounded-full h-8 px-3 text-[11px] font-bold border-teal-500/20 text-teal-600 hover:bg-teal-50 dark:border-teal-500/10 dark:text-teal-400 dark:hover:bg-teal-950/20 transition-all shadow-sm"
                                        >
                                            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                                        </Button>
                                    </div>
                                    <div className="flex bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg text-xs font-semibold">
                                        <button 
                                            type="button"
                                            onClick={() => setActiveTab("all")} 
                                            className={`px-3 py-1.5 rounded-md transition ${activeTab === "all" ? "bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-white" : "text-muted-foreground hover:text-foreground"}`}
                                        >
                                            All Logs
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => setActiveTab("present")} 
                                            className={`px-3 py-1.5 rounded-md transition ${activeTab === "present" ? "bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-white" : "text-muted-foreground hover:text-foreground"}`}
                                        >
                                            Present Today ({presentToday.length})
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => setActiveTab("absent")} 
                                            className={`px-3 py-1.5 rounded-md transition ${activeTab === "absent" ? "bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-white" : "text-muted-foreground hover:text-foreground"}`}
                                        >
                                            Absent Today ({absentToday.length})
                                        </button>
                                    </div>
                                </div>

                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" />
                                ) : activeTab === "absent" ? (
                                    absentToday.length === 0 ? (
                                        <EmptyState icon={Check} title="All employees are present" description="Great! Everyone has punched in for today." />
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                                        <th className="text-left p-3">Employee</th>
                                                        <th className="text-left p-3">Email</th>
                                                        <th className="text-left p-3">Department</th>
                                                        <th className="text-left p-3">Designation</th>
                                                        <th className="text-left p-3">Status</th>
                                                        {canManage && <th className="text-right p-3">Actions</th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {absentToday.map((emp) => (
                                                        <tr key={emp.id} className="border-t border-black/5 dark:border-white/5 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                                            <td className="p-3 font-medium text-slate-900 dark:text-slate-100">{emp.name}</td>
                                                            <td className="p-3 text-muted-foreground">{emp.email}</td>
                                                            <td className="p-3 text-slate-600 dark:text-slate-350">{emp.department || "—"}</td>
                                                            <td className="p-3 text-slate-600 dark:text-slate-350">{emp.designation || "—"}</td>
                                                            <td className="p-3">
                                                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-450">
                                                                    Absent
                                                                </span>
                                                            </td>
                                                            {canManage && (
                                                                <td className="p-3 text-right">
                                                                    <Button 
                                                                        variant="outline" 
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            setEditing({ user_id: emp.id, date: todayStr });
                                                                            setAdminOpen(true);
                                                                        }}
                                                                        className="rounded-full h-8 px-3 text-xs"
                                                                    >
                                                                        <Plus className="w-3.5 h-3.5 mr-1" /> Mark Present
                                                                    </Button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                ) : (() => {
                                    const dataset = activeTab === "present" ? presentToday : team;
                                    return dataset.length === 0 ? (
                                        <EmptyState icon={Clock} title="No attendance records" description="No attendance logs found for this filter." />
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                                        <th className="text-left p-3">Employee</th>
                                                        <th className="text-left p-3">Date</th>
                                                        <th className="text-left p-3">Punch In</th>
                                                        <th className="text-left p-3">Punch Out</th>
                                                        <th className="text-left p-3">Duration</th>
                                                        <th className="text-left p-3">Location</th>
                                                        <th className="text-left p-3">Status</th>
                                                        {canManage && <th className="text-right p-3">Actions</th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dataset.map((h) => (
                                                        <tr key={h.id} className="border-t border-black/5 dark:border-white/5 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                                            <td className="p-3 font-medium text-slate-900 dark:text-slate-100">{h.user_name}</td>
                                                            <td className="p-3">{h.date}</td>
                                                            <td className="p-3">{h.punch_in ? new Date(h.punch_in).toLocaleTimeString() : "—"}</td>
                                                            <td className="p-3">{h.punch_out ? new Date(h.punch_out).toLocaleTimeString() : "—"}</td>
                                                            <td className="p-3">{fmtMin(h.duration_minutes)}</td>
                                                            <td className="p-3">
                                                                {h.lat_in && h.lng_in ? (
                                                                    <a 
                                                                        href={`https://www.google.com/maps/search/?api=1&query=${h.lat_in},${h.lng_in}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-semibold"
                                                                        title="View punch-in location on Google Maps"
                                                                    >
                                                                        <MapPin className="w-3.5 h-3.5" />
                                                                        <span>In</span>
                                                                    </a>
                                                                ) : null}
                                                                {h.lat_out && h.lng_out ? (
                                                                    <a 
                                                                        href={`https://www.google.com/maps/search/?api=1&query=${h.lat_out},${h.lng_out}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-semibold ml-3"
                                                                        title="View punch-out location on Google Maps"
                                                                    >
                                                                        <MapPin className="w-3.5 h-3.5" />
                                                                        <span>Out</span>
                                                                    </a>
                                                                ) : null}
                                                                {!h.lat_in && !h.lat_out ? "—" : null}
                                                            </td>
                                                            <td className="p-3">{h.is_late ? <span className="text-xs text-warning font-semibold">Late</span> : <StatusBadge status={h.status} />}</td>
                                                            {canManage && (
                                                                <td className="p-3 text-right">
                                                                    <button onClick={() => { setEditing(h); setAdminOpen(true); }} className="p-2 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors" data-testid={`edit-attendance-${h.id}`}>
                                                                        <Pencil className="w-4 h-4" />
                                                                    </button>
                                                                    <button onClick={() => remove(h.id)} className="p-2 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors" data-testid={`delete-attendance-${h.id}`}>
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })()}
                            </>
                        );
                    })()}
                </GlassCard>
            )}

            <Dialog open={scannerOpen} onOpenChange={(open) => { setScannerOpen(open); if (!open) stopCamera(); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Scan Attendance QR Code</DialogTitle>
                    </DialogHeader>
                    
                    <div className="relative aspect-video rounded-xl bg-slate-950 overflow-hidden border border-black/5 dark:border-white/5">
                        <video ref={videoRef} className="w-full h-full object-cover" />
                        <canvas ref={canvasRef} className="hidden" />
                        
                        {/* Target reticle overlay */}
                        <div className="absolute inset-0 border-[3px] border-primary/40 m-8 rounded-xl pointer-events-none flex items-center justify-center">
                            <div className="w-8 h-8 border-t-4 border-l-4 border-primary absolute top-0 left-0" />
                            <div className="w-8 h-8 border-t-4 border-r-4 border-primary absolute top-0 right-0" />
                            <div className="w-8 h-8 border-b-4 border-l-4 border-primary absolute bottom-0 left-0" />
                            <div className="w-8 h-8 border-b-4 border-r-4 border-primary absolute bottom-0 right-0" />
                        </div>
                    </div>
                    
                    <div className="flex justify-between items-center gap-3 mt-4">
                        <Button
                            variant="outline"
                            onClick={() => setFacingMode(prev => prev === "user" ? "environment" : "user")}
                            className="text-xs"
                        >
                            Switch Camera
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => setScannerOpen(false)}
                            className="text-xs"
                        >
                            Cancel
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function timeFromIso(value) {
    if (!value) return "";
    return new Date(value).toTimeString().slice(0, 5);
}

function AttendanceForm({ employees, record, onSaved }) {
    const [form, setForm] = useState({
        user_id: record?.user_id || employees[0]?.id || "",
        date: record?.date || new Date().toISOString().slice(0, 10),
        punch_in: timeFromIso(record?.punch_in),
        punch_out: timeFromIso(record?.punch_out),
        status: record?.status || "present",
        is_late: !!record?.is_late,
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (record && record.id) {
                await api.patch(`/attendance/${record.id}`, form);
                toast.success("Attendance updated");
            } else {
                await api.post("/attendance/admin", form);
                toast.success("Attendance added");
            }
            onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-3">
            {!record?.id && (
                <div>
                    <Label>Employee</Label>
                    <select required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm" data-testid="attendance-employee">
                        {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>)}
                    </select>
                </div>
            )}
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" disabled={!!record?.id} /></div>
                <div>
                    <Label>Status</Label>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                        {["present", "absent", "half_day", "leave"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Punch in</Label><Input type="time" value={form.punch_in} onChange={(e) => setForm({ ...form, punch_in: e.target.value })} className="mt-1" /></div>
                <div><Label>Punch out</Label><Input type="time" value={form.punch_out} onChange={(e) => setForm({ ...form, punch_out: e.target.value })} className="mt-1" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_late} onChange={(e) => setForm({ ...form, is_late: e.target.checked })} />
                Mark as late
            </label>
            <Button type="submit" className="w-full rounded-full h-11" disabled={saving || (!record && !form.user_id)}>
                {saving ? "Saving…" : "Save attendance"}
            </Button>
        </form>
    );
}

function Row({ label, value, color = "text-foreground" }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{label}</span>
            <span className={`font-heading font-bold text-2xl ${color}`}>{value}</span>
        </div>
    );
}
